/**
 * ScreenHeader — the sticky header that sits above every screen's content.
 *
 * Behaviour:
 *   - Sticky (uses CSS, not JS scroll listeners — saves frame budget)
 *   - Backdrop-blurred so content scrolls behind it without a hard cutoff
 *   - Hosts a title, optional eyebrow tag, and a slot for trailing actions
 *
 * Note for the team: do not stack two scrollable containers inside a screen
 * that uses this header — the sticky positioning relies on the parent flex
 * column being the only scroll context.
 */
export default function ScreenHeader({ eyebrow, title, subtitle, trailing }) {
  return (
    <div
      className="sticky top-0 z-30 -mx-5 mb-2 border-b border-stroke/60 bg-bg-base/85 px-5 pb-3 pt-4 backdrop-blur-md backdrop-saturate-150"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)' }}
    >
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow ? (
            <div className="mb-1 flex items-center gap-2 text-2xs font-semibold uppercase tracking-widest-plus text-f1-red">
              <span className="inline-block h-1.5 w-1.5 animate-pulse-red rounded-full bg-f1-red" />
              {eyebrow}
            </div>
          ) : null}
          <h1 className="truncate text-2xl font-bold leading-tight text-ink-primary">{title}</h1>
          {subtitle ? (
            <p className="mt-0.5 truncate text-xs text-ink-secondary">{subtitle}</p>
          ) : null}
        </div>
        {trailing ? <div className="flex shrink-0 items-center gap-2">{trailing}</div> : null}
      </div>
    </div>
  );
}
